<template>
    <div class="min-h-screen bg-gray-100 overflow-x-hidden">
        <AdminNavbar active="mrr" />

        <div class="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
            <h1 class="text-base sm:text-xl font-bold text-gray-800 mb-3 sm:mb-6">Receita Mensal Recorrente</h1>

            <div v-if="error" class="text-red-600 text-sm mb-4">{{ error }}</div>

            <!-- Summary cards -->
            <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <template v-if="loading">
                    <div v-for="i in 4" :key="i" class="bg-white border border-gray-100 shadow-sm rounded-xl p-4 sm:p-5 min-w-0 overflow-hidden">
                        <div class="skeleton h-3 w-24 rounded mb-2"></div>
                        <div class="skeleton h-7 w-16 rounded"></div>
                    </div>
                </template>
                <template v-else>
                    <div class="bg-white border border-gray-100 shadow-sm rounded-xl p-4 sm:p-5 min-w-0 overflow-hidden">
                        <div class="text-xs text-gray-400 mb-1">Receita Mensal</div>
                        <div class="text-xl sm:text-2xl font-bold text-emerald-600 break-words">{{ formatBrl(mrr.mrr_brl) }}</div>
                    </div>
                    <div class="bg-white border border-gray-100 shadow-sm rounded-xl p-4 sm:p-5 min-w-0 overflow-hidden">
                        <div class="text-xs text-gray-400 mb-1">Assinantes ativos</div>
                        <div class="text-xl sm:text-2xl font-bold text-gray-800">{{ mrr.subscriber_count }}</div>
                    </div>
                    <div class="bg-white border border-gray-100 shadow-sm rounded-xl p-4 sm:p-5 min-w-0 overflow-hidden">
                        <div class="text-xs text-gray-400 mb-1">Pagando preço cheio</div>
                        <div class="text-xl sm:text-2xl font-bold text-cyan-600">{{ mrr.full_price_count }}</div>
                    </div>
                    <div class="bg-white border border-gray-100 shadow-sm rounded-xl p-4 sm:p-5 min-w-0 overflow-hidden">
                        <div class="text-xs text-gray-400 mb-1">Com desconto</div>
                        <div class="text-xl sm:text-2xl font-bold text-amber-600">{{ mrr.discounted_count }}</div>
                    </div>
                </template>
            </div>

            <!-- Loading skeleton (shared) -->
            <div v-if="loading" class="space-y-3">
                <div v-for="i in 4" :key="i" class="skeleton h-20 w-full rounded-xl"></div>
            </div>

            <template v-else>
                <div v-if="mrr.subscriptions.length === 0" class="bg-white border border-gray-100 rounded-xl py-8 text-center text-gray-400 text-sm">
                    Nenhuma assinatura ativa.
                </div>

                <!-- Desktop table -->
                <div v-else class="hidden sm:block overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
                    <table class="table table-sm w-full min-w-[900px]">
                        <thead>
                            <tr class="text-[11px] uppercase tracking-wide">
                                <th class="text-left !text-black">Cliente</th>
                                <th class="text-center !text-black">Preço cheio</th>
                                <th class="text-center !text-black">Desconto</th>
                                <th class="text-center !text-black">Valor efetivo</th>
                                <th class="text-center !text-black">Cupom</th>
                                <th class="text-center !text-black">Próx. renovação</th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-for="sub in mrr.subscriptions" :key="sub.stripe_subscription_id">
                                <tr
                                    class="hover:bg-gray-50 transition-colors cursor-pointer"
                                    @click="toggleExpand(sub.stripe_subscription_id)"
                                >
                                    <td>
                                        <div class="font-medium text-gray-800">{{ sub.company_name }}</div>
                                        <div class="text-xs text-gray-400">{{ sub.customer_email }}</div>
                                    </td>
                                    <td class="text-center text-gray-600">
                                        {{ formatBrl(sub.full_price_cents / 100) }}
                                    </td>
                                    <td class="text-center">
                                        <span v-if="sub.discount_type === 'percent_off'" :class="adminBadgeClass('warning')">
                                            {{ sub.discount_value }}% off
                                        </span>
                                        <span v-else-if="sub.discount_type === 'amount_off'" :class="adminBadgeClass('warning')">
                                            -{{ formatBrl(sub.discount_value / 100) }}
                                        </span>
                                        <span v-else :class="adminBadgeClass('success')">Cheio</span>
                                    </td>
                                    <td class="text-center font-semibold text-emerald-600">
                                        {{ formatBrl(sub.effective_price_cents / 100) }}
                                    </td>
                                    <td class="text-center">
                                        <span v-if="sub.coupon_name" class="text-xs font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                                            {{ sub.coupon_name }}
                                        </span>
                                        <span v-else class="text-gray-300">—</span>
                                    </td>
                                    <td class="text-center text-xs text-gray-400 whitespace-nowrap">
                                        {{ sub.current_period_end ? formatDate(sub.current_period_end) : '—' }}
                                    </td>
                                </tr>
                                <!-- Expanded row -->
                                <tr v-if="expanded.includes(sub.stripe_subscription_id)" class="bg-gray-50">
                                    <td colspan="6">
                                        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs text-gray-400">
                                            <div>
                                                <span class="text-gray-400">Stripe Customer ID:</span>
                                                <span class="ml-1 font-mono text-gray-600">{{ sub.stripe_customer_id }}</span>
                                            </div>
                                            <div>
                                                <span class="text-gray-400">Subscription ID:</span>
                                                <span class="ml-1 font-mono text-gray-600">{{ sub.stripe_subscription_id }}</span>
                                            </div>
                                            <div>
                                                <span class="text-gray-400">Início do período:</span>
                                                <span class="ml-1 text-gray-600">{{ sub.current_period_start ? formatDate(sub.current_period_start) : '—' }}</span>
                                            </div>
                                            <div v-if="sub.coupon_id">
                                                <span class="text-gray-400">Coupon ID:</span>
                                                <span class="ml-1 font-mono text-gray-600">{{ sub.coupon_id }}</span>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </div>

                <!-- Mobile cards -->
                <div v-if="mrr.subscriptions.length > 0" class="sm:hidden space-y-3">
                    <div
                        v-for="sub in mrr.subscriptions"
                        :key="sub.stripe_subscription_id"
                        class="bg-white border border-gray-100 rounded-xl shadow-sm p-4 min-w-0 overflow-hidden"
                        @click="toggleExpand(sub.stripe_subscription_id)"
                    >
                        <div class="space-y-2 mb-2 min-w-0">
                            <div class="min-w-0">
                                <div class="font-medium text-gray-800 truncate">{{ sub.company_name }}</div>
                                <div class="text-xs text-gray-400 truncate">{{ sub.customer_email }}</div>
                            </div>
                            <div class="flex flex-wrap items-center gap-2">
                                <span v-if="sub.discount_type === 'percent_off'" :class="adminBadgeClass('warning')">{{ sub.discount_value }}% off</span>
                                <span v-else-if="sub.discount_type === 'amount_off'" :class="adminBadgeClass('warning')">-{{ formatBrl(sub.discount_value / 100) }}</span>
                                <span v-else :class="adminBadgeClass('success')">Cheio</span>
                            </div>
                        </div>
                        <div class="grid grid-cols-2 gap-2 text-sm">
                            <div>
                                <div class="text-[11px] uppercase text-gray-400">Preço cheio</div>
                                <div class="text-gray-600">{{ formatBrl(sub.full_price_cents / 100) }}</div>
                            </div>
                            <div>
                                <div class="text-[11px] uppercase text-gray-400">Valor efetivo</div>
                                <div class="font-semibold text-emerald-600">{{ formatBrl(sub.effective_price_cents / 100) }}</div>
                            </div>
                            <div>
                                <div class="text-[11px] uppercase text-gray-400">Cupom</div>
                                <div class="font-mono text-xs text-gray-600">{{ sub.coupon_name || '—' }}</div>
                            </div>
                            <div>
                                <div class="text-[11px] uppercase text-gray-400">Próx. renovação</div>
                                <div class="text-xs text-gray-500">{{ sub.current_period_end ? formatDate(sub.current_period_end) : '—' }}</div>
                            </div>
                        </div>
                        <div v-if="expanded.includes(sub.stripe_subscription_id)" class="mt-3 pt-3 border-t border-gray-100 space-y-1 text-xs text-gray-400">
                            <div><span class="text-gray-400">Stripe Customer ID:</span> <span class="font-mono text-gray-600 break-all">{{ sub.stripe_customer_id }}</span></div>
                            <div><span class="text-gray-400">Subscription ID:</span> <span class="font-mono text-gray-600 break-all">{{ sub.stripe_subscription_id }}</span></div>
                            <div><span class="text-gray-400">Início do período:</span> <span class="text-gray-600">{{ sub.current_period_start ? formatDate(sub.current_period_start) : '—' }}</span></div>
                            <div v-if="sub.coupon_id"><span class="text-gray-400">Coupon ID:</span> <span class="font-mono text-gray-600 break-all">{{ sub.coupon_id }}</span></div>
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import AdminStripeService from '@/services/admin/AdminStripeService';
import { adminBadgeClass } from '@/constants/adminBadge';
import AdminNavbar from '@/components/admin/AdminNavbar.vue';

export default {
    name: 'AdminMrr',
    components: { AdminNavbar },
    setup() {
        const mrr = ref({ mrr_brl: 0, subscriber_count: 0, full_price_count: 0, discounted_count: 0, subscriptions: [] });
        const loading = ref(true);
        const error = ref('');
        const expanded = ref([]);

        onMounted(async () => {
            try {
                const { data } = await AdminStripeService.getMrr();
                mrr.value = data.data;
            } catch {
                error.value = 'Erro ao carregar dados do Stripe';
            } finally {
                loading.value = false;
            }
        });

        function toggleExpand(id) {
            const idx = expanded.value.indexOf(id);
            if (idx === -1) expanded.value.push(id);
            else expanded.value.splice(idx, 1);
        }

        function formatBrl(value) {
            return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
        }

        function formatDate(unixTs) {
            return new Date(unixTs * 1000).toLocaleDateString('pt-BR');
        }

        return { mrr, loading, error, expanded, toggleExpand, formatBrl, formatDate, adminBadgeClass };
    }
};
</script>
